import { t } from "i18next";
import { NotesTable } from "@/common";
import { ContactsBar } from "@/common/ContactsBar";
import { DEFAULT_NOTE_TYPES, DEFAULT_NOTE_TYPES_LABELS } from "@/services/api";
import { CalendarViewDashboard, CalendarViewMobileDashboard } from "@/features/calendar";
import { NotesTableMobile } from "@/common/NotesTable/mobile";
import { ResponsiveBlockSwitcher } from "@/shared/Components";
import type { Widget, WidgetContent } from "../types";
import type { NotesTableRecord } from "@/common/NotesTable/types";
import { DashboardsNotesTable } from "../Components/DashboardsNotesTable";
import { DashboardsCommitmentsTable } from "../Components/DashboardsCommitmentsTable";

export const WIDGET_CONTENTS_IDS = {
  COMMITMENTS_TABLE: 1,
  NOTES_TABLE: 2,
  CALENDAR: 3,
  CONTACTS_BAR: 4,
  CALENDAR_MOBILE: 5,
};

export const GRID_MAX_COLS_COUNT = 100;

export const WIDGETS = {
  [WIDGET_CONTENTS_IDS.COMMITMENTS_TABLE]: {
    id: WIDGET_CONTENTS_IDS.COMMITMENTS_TABLE,
    title: "Commitments",
    maxW: GRID_MAX_COLS_COUNT,
    minW: 37,
    minH: 13,
    maxH: Infinity,
    content: () => (
      <NotesTable
        isColorize
        scrollHeight={0}
        noteTitleHeaderText={DEFAULT_NOTE_TYPES_LABELS.DEFAULT_COMMITMENT}
        noteType={DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT}
        desktopColumns={[
          "important",
          "noteDate",
          "dueDate",
          "direction",
          "person",
          "noteTitle",
          "status",
        ]}
      />
    ),
  },
  [WIDGET_CONTENTS_IDS.NOTES_TABLE]: {
    id: WIDGET_CONTENTS_IDS.NOTES_TABLE,
    title: "Notes",
    maxW: GRID_MAX_COLS_COUNT,
    minW: 19,
    minH: 13,
    maxH: Infinity,
    content: () => (
      <NotesTable
        scrollHeight={0}
        virtual
        noteTitleHeaderText={DEFAULT_NOTE_TYPES_LABELS.DEFAULT_NOTE}
        noteType={DEFAULT_NOTE_TYPES.DEFAULT_NOTE}
        desktopColumns={["important", "noteDate", "person", "type", "titleWithStatus"]}
      />
    ),
  },
  [WIDGET_CONTENTS_IDS.CALENDAR]: {
    id: WIDGET_CONTENTS_IDS.CALENDAR,
    title: "Calendar",
    maxW: GRID_MAX_COLS_COUNT,
    minW: 25,
    minH: 20,
    maxH: Infinity,
    content: () => <CalendarViewDashboard />,
  },
  [WIDGET_CONTENTS_IDS.CALENDAR_MOBILE]: {
    id: WIDGET_CONTENTS_IDS.CALENDAR_MOBILE,
    title: "Calendar",
    maxW: GRID_MAX_COLS_COUNT,
    minW: 25,
    minH: 20,
    maxH: Infinity,
    content: () => <CalendarViewMobileDashboard />,
  },
  [WIDGET_CONTENTS_IDS.CONTACTS_BAR]: {
    id: WIDGET_CONTENTS_IDS.CONTACTS_BAR,
    title: "Contacts",
    maxW: GRID_MAX_COLS_COUNT,
    minW: 23,
    minH: 13,
    maxH: Infinity,
    content: () => <ContactsBar />,
  },
};

export const getWidgetContent = ({
  widgetContentId,
}: {
  widgetContentId: number;
  containerHeight: number;
  onNotesTableRecordClick?: (record: NotesTableRecord) => void;
}): WidgetContent => {
  switch (widgetContentId) {
    case WIDGET_CONTENTS_IDS.COMMITMENTS_TABLE:
      return {
        id: WIDGET_CONTENTS_IDS.COMMITMENTS_TABLE,
        title: t("common.noteType.commitment", { count: 2 }),
        maxW: GRID_MAX_COLS_COUNT,
        minW: 37,
        minH: 13,
        maxH: Infinity,
        content: ({ directionFilter }, widget) => (
          <ResponsiveBlockSwitcher
            mobile={<NotesTableMobile widget noteType={DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT} />}
            desktop={
              <DashboardsCommitmentsTable directionFilter={directionFilter} widgetId={widget?.id} />
            }
          />
        ),
      };
    case WIDGET_CONTENTS_IDS.NOTES_TABLE:
      return {
        id: WIDGET_CONTENTS_IDS.NOTES_TABLE,
        title: t("common.noteType.note", { count: 2 }),
        maxW: GRID_MAX_COLS_COUNT,
        minW: 19,
        minH: 13,
        maxH: Infinity,
        content: (_, widget) => (
          <ResponsiveBlockSwitcher
            mobile={<NotesTableMobile widget />}
            desktop={<DashboardsNotesTable widgetId={widget?.id} />}
          />
        ),
      };
    case WIDGET_CONTENTS_IDS.CALENDAR:
      return {
        id: WIDGET_CONTENTS_IDS.CALENDAR,
        title: t("common.calendar"),
        maxW: GRID_MAX_COLS_COUNT,
        minW: 25,
        minH: 20,
        maxH: Infinity,
        content: () => <CalendarViewDashboard />,
      };

    case WIDGET_CONTENTS_IDS.CALENDAR_MOBILE:
      return {
        id: WIDGET_CONTENTS_IDS.CALENDAR_MOBILE,
        title: "Calendar",
        maxW: GRID_MAX_COLS_COUNT,
        minW: 25,
        minH: 20,
        maxH: Infinity,
        content: () => <CalendarViewMobileDashboard />,
      };

    case WIDGET_CONTENTS_IDS.CONTACTS_BAR:
      return {
        id: WIDGET_CONTENTS_IDS.CONTACTS_BAR,
        title: t("common.contacts"),
        maxW: GRID_MAX_COLS_COUNT,
        minW: 23,
        minH: 13,
        maxH: Infinity,
        content: () => <ContactsBar />,
      };
    default:
      return {
        id: 0,
        title: t("common.error"),
        maxW: 1,
        minW: 1,
        minH: 1,
        maxH: 1,
        content: () => <>{t("common.error")}</>,
      };
  }
};

export const MobileWidgetsArray: Widget[] = [
  { id: "5", x: 1, y: 1, w: 20, h: 600, widgetContentId: 5 },
  { id: "1", x: 1, y: 1, w: 20, h: 600, widgetContentId: 1 },
  { id: "2", x: 1, y: 1, w: 20, h: 600, widgetContentId: 2 },
  { id: "4", x: 1, y: 1, w: 20, h: 600, widgetContentId: 4 },
];
